import { Action, Dispatch } from 'redux'
import { push } from 'connected-react-router'
import { State } from 'index'
import { db, FirebaseDocRefType, FirebaseTimestamp, FirebaseTimestampType, FirebaseWriteBatch } from 'firebase/index'
import { format, isSameDay, subMinutes } from 'date-fns'
import { BreadType } from 'types/bread'
import { OrderType } from 'types/checkout'
import { StockType, StockUnionType } from 'types/stock'
import { ScheduleType } from 'types/schedule'
import { hideLoadingAction, showLoadingAction } from 'reducks/loading/actions'
import { dateToString, parseToDate } from 'lib/helper'
import { DaysOfWeek } from 'lib/daysOfWeek'
import { sendNewStoreMessage } from 'reducks/messages/operations'
import { publishFirstStock } from 'repos/store'
import { updatePublishFirstStockAction } from 'reducks/store/actions'
import { StoreCalendarType } from 'types/storeCalendar'
import { addNotifiedTypes } from 'reducks/bread/operations'
import { addStoreStockMessageQueue } from 'reducks/storeStockMessageQueue/operations'
const daysOfWeek = new DaysOfWeek()

const addPreorderStocks = (
  activeDates: Date[],
  bread: BreadType,
  orders: OrderType[],
  currentStocks: StockType[],
  schedules: ScheduleType[],
  individualSettingDates: Date[],
  preorderStartedAt: string,
  type: StockUnionType
) => {
  return async (dispatch: Dispatch<Action>, getState: () => State) => {
    if (!bread.bread_ref) return
    const closedDates = getState().calendar.closedDates
    const specialHolidays = getState().calendar.specialHolidays
    const storeCalendars: StoreCalendarType[] = getState().storeCalendar.storeCalendars
    const temporaryOpenCalendars = storeCalendars.filter((storeCalendar) => storeCalendar.status === 'open')
    const storeName = getState().store.name
    const storeRef = getState().auth.store_ref
    const storeStatus = getState().store.status
    const isBeforePublishingStock = getState().store.is_before_publishing_stock
    // 500件以上のbatch処理に対応できるように
    const batchArray: FirebaseWriteBatch[] = []
    batchArray.push(db.batch())
    let operationCounter = 0
    let batchIndex = 0
    const nextStockRefs: FirebaseDocRefType[] = []
    dispatch(showLoadingAction())
    // 事前予約設定のオプション
    const value: { preorder_started_at?: FirebaseTimestampType } = {}
    if (preorderStartedAt !== '') {
      value.preorder_started_at = FirebaseTimestamp.fromDate(new Date(preorderStartedAt))
    }
    // 事前予約の受渡可能日を追加する
    for (let i = 0; i < activeDates.length; i = (i + 1) | 0) {
      const activeDate = activeDates[i]
      const date = format(activeDate, 'yyyyMMdd')
      // 個別設定されている受渡可能日は除外
      if (individualSettingDates.some((date) => isSameDay(date, activeDate))) continue
      // 休業日の場合は除外
      if (closedDates.some((closedDate) => isSameDay(closedDate, activeDate))) continue
      for (let j = 0; j < schedules.length; j = (j + 1) | 0) {
        const schedule = schedules[j]
        // 事前予約のオプション設定を更新する
        if (schedule.schedule_ref) {
          batchArray[batchIndex].update(schedule.schedule_ref, value)
          operationCounter++
          if (operationCounter === 499) {
            batchArray.push(db.batch())
            batchIndex++
            operationCounter = 0
          }
        }
        // 臨時営業日の引き継ぎ元曜日を取得する
        const dayOfWeek = daysOfWeek.getDayOfWeek(activeDate, specialHolidays)
        const temporaryOpen = temporaryOpenCalendars.find(
          (storeCalendar) => storeCalendar.date === dateToString(activeDate)
        )
        const defaultDayOfWeek = temporaryOpen?.default_day_of_week || dayOfWeek
        // 事前予約設定されている曜日でなければ在庫を追加しない
        if (!schedule.days_of_week.includes(defaultDayOfWeek)) continue
        const deadline = FirebaseTimestamp.fromDate(subMinutes(parseToDate(date), schedule.hours_to_deadline * 60))
        // 受渡解禁時刻ごとにstockを追加する
        schedule.default_stocks.map((defaultStock) => {
          const stockValue: StockType = {
            bread_ref: schedule.bread_ref,
            bread_name: bread.name,
            date: date,
            choices: schedule.choices,
            deadline: deadline,
            image_url: bread.image_urls[0] || '',
            is_deleted: false,
            last_notified_time: null,
            maximum_order: schedule.maximum_order,
            note: schedule.note,
            options: schedule.options,
            pickupable_time: defaultStock.pickupable_time,
            price: bread.price,
            stock: defaultStock.stock,
            store_name: storeName,
            store_ref: storeRef,
            type: schedule.type,
          }
          // sacri stationの場合、販売元店舗のrefを追加する
          if (typeof schedule.manufacturer_ref !== 'undefined') {
            stockValue.manufacturer_ref = schedule.manufacturer_ref
          }
          const ref = storeRef
            .collection('store_stocks')
            .doc(`stock_${schedule.type}_${schedule.schedule_ref?.id}-${date}_${defaultStock.pickupable_time}`)
          nextStockRefs.push(ref)
          batchArray[batchIndex].set(ref, stockValue, { merge: true })
          operationCounter++
          if (operationCounter === 499) {
            batchArray.push(db.batch())
            batchIndex++
            operationCounter = 0
          }
        })
      }
    }
    // 削除・上書き対象かの判定
    for (let i = 0; i < currentStocks.length; i = (i + 1) | 0) {
      const currentStock = currentStocks[i]
      if (!currentStock.stock_ref) continue
      // 更新後のstocksに存在しているので削除しない
      if (nextStockRefs.some((nextStockRef) => currentStock.stock_ref?.id === nextStockRef.id)) continue
      // 注文がある日付であれば在庫数を0に変更
      if (orders.some((order) => currentStock.date === order.date)) {
        const ref = currentStock.stock_ref
        delete currentStock.stock_ref
        batchArray[batchIndex].set(ref, { stock: 0 }, { merge: true })
        operationCounter++
        if (operationCounter === 499) {
          batchArray.push(db.batch())
          batchIndex++
          operationCounter = 0
        }
        continue
      }
      // 個別設定されている受渡可能日は削除しない
      if (individualSettingDates.some((date) => isSameDay(date, parseToDate(currentStock.date)))) continue
      // 在庫データを削除
      batchArray[batchIndex].delete(currentStock.stock_ref)
      operationCounter++
      if (operationCounter === 499) {
        batchArray.push(db.batch())
        batchIndex++
        operationCounter = 0
      }
    }
    // 店舗が初めて在庫を公開する場合、5km圏内のユーザーにPush通知を送る
    if (isBeforePublishingStock && storeStatus === 'public') {
      await publishFirstStock(storeRef)
      dispatch(updatePublishFirstStockAction())
      await sendNewStoreMessage(storeRef)
    } else {
      // この商品で初めて在庫を設定する場合、ユーザーに通知を送る
      if (!bread.notified_types?.includes(type)) {
        await Promise.all([
          addNotifiedTypes({ bread, type }),
          addStoreStockMessageQueue({ breadRef: bread.bread_ref, storeRef }),
        ])
      }
    }
    try {
      await batchArray.forEach(async (batch) => await batch.commit())
      const completeUrl = `/schedule/${bread.bread_ref?.id}/complete`
      dispatch(push(completeUrl))
      dispatch(hideLoadingAction())
    } catch (e) {
      console.error(e)
      dispatch(hideLoadingAction())
    }
  }
}

export default addPreorderStocks
